import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DEPARTMENTS, NATURE_OF_EMPLOYMENT, EMPLOYMENT_TYPES, PAY_INTERVALS, MARITAL_STATUS, REST_DAYS } from "../mock";

const empty = {
  employee_number: "", first_name: "", last_name: "", sex: "Male", date_of_birth: "",
  marital_status: "Single", national_id: "", address: "", phone: "", email: "",
  next_of_kin_name: "", next_of_kin_address: "", next_of_kin_relationship: "",
  department: "Operations", job_title: "", nature_of_employment: "Permanent",
  employment_type: "Full-time", commencement_date: "", probation_end_date: "", notice_period: "1 month",
  wage_rate: "", pay_interval: "Monthly", wage_components: "",
  annual_leave_entitlement: 15, sick_leave_entitlement: 14, public_holidays_entitlement: 12,
  weekly_rest_day: "Sunday", daily_hours: 8, weekly_hours: 40, night_work: false,
  status: "Active", termination_date: "", termination_reason: "", terminal_payments: "",
};

function Section({ title, hint, children }) {
  return (
    <div className="border-t border-slate-100 pt-5 mt-5 first:border-0 first:pt-0 first:mt-0">
      <h4 className="font-display font-bold text-brand-navy">{title}</h4>
      {hint && <p className="text-xs text-slate-400 mb-3">{hint}</p>}
      <div className="grid sm:grid-cols-2 gap-4 mt-3">{children}</div>
    </div>
  );
}
function Field({ label, children, full }) {
  return (
    <div className={`grid gap-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
function Sel({ value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
    </Select>
  );
}

export default function EmployeeForm({ open, onOpenChange, initial, onSave }) {
  const [form, setForm] = useState(empty);
  useEffect(() => { setForm(initial ? { ...empty, ...initial } : empty); }, [initial, open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setV = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form, wage_rate: Number(form.wage_rate) || 0 });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0">
        <div className="px-6 py-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <SheetHeader>
            <SheetTitle className="font-display text-xl text-brand-navy">{initial ? "Edit employee" : "Add employee"}</SheetTitle>
            <SheetDescription>Records aligned to Schedule 4 (reg 22/23) of the Employment & Labour Relations Regulations.</SheetDescription>
          </SheetHeader>
        </div>
        <form onSubmit={submit} className="px-6 py-5">
          <Section title="Identity">
            <Field label="Employee number"><Input value={form.employee_number} onChange={set("employee_number")} placeholder="KTC-007" required /></Field>
            <Field label="Status"><Sel value={form.status} onChange={setV("status")} options={["Active", "Terminated"]} /></Field>
            <Field label="First name"><Input value={form.first_name} onChange={set("first_name")} required /></Field>
            <Field label="Last name"><Input value={form.last_name} onChange={set("last_name")} required /></Field>
            <Field label="Sex"><Sel value={form.sex} onChange={setV("sex")} options={["Male", "Female", "Other"]} /></Field>
            <Field label="Date of birth"><Input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} /></Field>
            <Field label="Marital status"><Sel value={form.marital_status} onChange={setV("marital_status")} options={MARITAL_STATUS} /></Field>
            <Field label="National ID / Omang"><Input value={form.national_id} onChange={set("national_id")} /></Field>
            <Field label="Residential address" full><Input value={form.address} onChange={set("address")} /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={set("phone")} placeholder="+267 ..." /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} /></Field>
          </Section>

          <Section title="Next of kin">
            <Field label="Full name"><Input value={form.next_of_kin_name} onChange={set("next_of_kin_name")} /></Field>
            <Field label="Relationship"><Input value={form.next_of_kin_relationship} onChange={set("next_of_kin_relationship")} /></Field>
            <Field label="Address" full><Input value={form.next_of_kin_address} onChange={set("next_of_kin_address")} /></Field>
          </Section>

          <Section title="Employment">
            <Field label="Department"><Sel value={form.department} onChange={setV("department")} options={DEPARTMENTS} /></Field>
            <Field label="Job title"><Input value={form.job_title} onChange={set("job_title")} /></Field>
            <Field label="Nature of employment"><Sel value={form.nature_of_employment} onChange={setV("nature_of_employment")} options={NATURE_OF_EMPLOYMENT} /></Field>
            <Field label="Employment type"><Sel value={form.employment_type} onChange={setV("employment_type")} options={EMPLOYMENT_TYPES} /></Field>
            <Field label="Commencement date"><Input type="date" value={form.commencement_date} onChange={set("commencement_date")} /></Field>
            <Field label="Probation end date"><Input type="date" value={form.probation_end_date} onChange={set("probation_end_date")} /></Field>
            <Field label="Notice period"><Input value={form.notice_period} onChange={set("notice_period")} placeholder="e.g. 1 month" /></Field>
          </Section>

          <Section title="Wages" hint="Wage rate, pay interval and components (basic + allowances).">
            <Field label="Wage rate (BWP)"><Input type="number" value={form.wage_rate} onChange={set("wage_rate")} /></Field>
            <Field label="Pay interval"><Sel value={form.pay_interval} onChange={setV("pay_interval")} options={PAY_INTERVALS} /></Field>
            <Field label="Wage components" full><Textarea value={form.wage_components} onChange={set("wage_components")} placeholder="Basic P8,000; Transport P800; Housing P1,000" /></Field>
            <div className="sm:col-span-2 flex items-center justify-between rounded-xl bg-brand-mist px-4 py-3">
              <div><div className="text-sm font-medium text-brand-navy">Night-work</div><div className="text-xs text-slate-500">Adds 5% of basic salary allowance (reg 24)</div></div>
              <Switch checked={form.night_work} onCheckedChange={setV("night_work")} />
            </div>
          </Section>

          <Section title="Leave, hours & rest">
            <Field label="Annual leave (days / 12 months)"><Input type="number" value={form.annual_leave_entitlement} onChange={set("annual_leave_entitlement")} /></Field>
            <Field label="Sick leave (days / year)"><Input type="number" value={form.sick_leave_entitlement} onChange={set("sick_leave_entitlement")} /></Field>
            <Field label="Public holidays (days / year)"><Input type="number" value={form.public_holidays_entitlement} onChange={set("public_holidays_entitlement")} /></Field>
            <Field label="Weekly rest day"><Sel value={form.weekly_rest_day} onChange={setV("weekly_rest_day")} options={REST_DAYS} /></Field>
            <Field label="Daily hours"><Input type="number" value={form.daily_hours} onChange={set("daily_hours")} /></Field>
            <Field label="Weekly hours"><Input type="number" value={form.weekly_hours} onChange={set("weekly_hours")} /></Field>
          </Section>

          {form.status === "Terminated" && (
            <Section title="Termination">
              <Field label="Termination date"><Input type="date" value={form.termination_date} onChange={set("termination_date")} /></Field>
              <Field label="Reason"><Input value={form.termination_reason} onChange={set("termination_reason")} /></Field>
              <Field label="Terminal payments" full><Textarea value={form.terminal_payments} onChange={set("terminal_payments")} placeholder="Wages, pay in lieu of notice, leave pay, severance benefit..." /></Field>
            </Section>
          )}

          <div className="sticky bottom-0 bg-white border-t border-slate-100 -mx-6 px-6 py-4 mt-6 flex gap-3">
            <Button type="button" variant="outline" className="flex-1 rounded-lg" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="flex-1 bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg">{initial ? "Save changes" : "Add employee"}</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
